/**
 * @fileoverview Firm default for the day-one primary contact person
 * (MOR-2342). Rendered inside the Contact Information preset on
 * Settings → Fields:
 * owners / admins choose whether `contact_name` / `contact_email` /
 * `contact_phone` default to the Contact preset, the analyst preparing the
 * documents, the lead appointee, or a nominated staff member. The generate
 * wizard follows this choice and lets the analyst override it per run.
 */

import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { User } from "lucide-react";
import { toast } from "sonner";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import type {
  DayOneContactDefault,
  DayOneContactDefaultMode,
} from "@cvx/organizations/schema";
import { CONTACT_PERSON_MODE_LABELS } from "~/convex/jobs/precedents/contactPerson";
import {
  CONTACT_PERSON_MODES,
  CONTACT_PERSON_MODE_ICONS,
} from "@/components/settings/precedents/contact-person-modes";
import { RoleBadge } from "@/components/settings/tasks/RoleBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { extractErrorMessage } from "@/utils/errors";

const MODE_HELP: Record<DayOneContactDefaultMode, string> = {
  firm_preset: "Use the Contact preset's name, email and phone.",
  preparing_user:
    "Whoever generates the documents — so the direct line changes with the analyst.",
  appointee:
    "The lead appointee, with their own email and phone when they are a member.",
  staff_member: "One nominated person for every job.",
  job_role:
    "Whoever is allocated to the chosen job role (for example the Manager) on each job.",
};

/** Both dropdowns share one width so the second lines up under the first. */
const SELECT_WIDTH = "w-80";

export function DayOneContactDefaultCard({
  orgId,
  embedded = false,
}: {
  orgId: Id<"organizations">;
  /**
   * Compact layout for the Contact Information preset form, without the
   * standalone card chrome used when the control sat above the category.
   */
  embedded?: boolean;
}) {
  const { data: setting } = useQuery(
    convexQuery(
      api.settings.precedents.contactDefaults.getDayOneContactDefault,
      {
        orgId,
      },
    ),
  );
  const { data: candidates } = useQuery(
    convexQuery(
      api.settings.precedents.contactDefaults.listContactPersonCandidates,
      { orgId },
    ),
  );
  const { data: workflowRoles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );
  const { mutateAsync: save, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.contactDefaults.setDayOneContactDefault,
    ),
  });

  const current: DayOneContactDefault = setting ?? { mode: "firm_preset" };

  const persist = (next: DayOneContactDefault) => {
    void save({ orgId, setting: next })
      .then(() => toast.success("Primary contact default saved"))
      .catch((error: unknown) =>
        toast.error(extractErrorMessage(error, "Failed to save")),
      );
  };

  const controls = (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Select
          value={current.mode}
          disabled={isPending}
          onValueChange={(value) => {
            const mode = value as DayOneContactDefaultMode;
            if (mode === "staff_member") {
              const userId = current.userId ?? candidates?.[0]?.userId;
              if (!userId) {
                toast.error("Add a member to the firm first");
                return;
              }
              persist({ mode, userId });
              return;
            }
            if (mode === "job_role") {
              const roleId = current.roleId ?? workflowRoles?.[0]?._id;
              if (!roleId) {
                toast.error("Create a workflow role first");
                return;
              }
              persist({ mode, roleId });
              return;
            }
            persist({ mode });
          }}
        >
          <SelectTrigger className={SELECT_WIDTH}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONTACT_PERSON_MODES.map((mode) => {
              const Icon = CONTACT_PERSON_MODE_ICONS[mode];
              return (
                <SelectItem key={mode} value={mode}>
                  <span className="inline-flex items-center gap-2">
                    <Icon className="size-4 shrink-0 text-primary/60" />
                    {CONTACT_PERSON_MODE_LABELS[mode]}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {current.mode === "staff_member" && (
          <Select
            value={current.userId ?? ""}
            disabled={isPending}
            onValueChange={(userId) =>
              persist({
                mode: "staff_member",
                userId: userId as Id<"users">,
              })
            }
          >
            <SelectTrigger className={SELECT_WIDTH}>
              <SelectValue placeholder="Choose a staff member" />
            </SelectTrigger>
            <SelectContent>
              {(candidates ?? []).map((c) => (
                <SelectItem key={c.userId} value={c.userId}>
                  {c.name}
                  {c.phone ? ` · ${c.phone}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {current.mode === "job_role" && (
        <div className="mt-3">
          <Select
            value={current.roleId ?? ""}
            disabled={isPending}
            onValueChange={(roleId) =>
              persist({
                mode: "job_role",
                roleId: roleId as Id<"workflowRoles">,
              })
            }
          >
            <SelectTrigger className={SELECT_WIDTH}>
              <SelectValue placeholder="Choose a job role" />
            </SelectTrigger>
            <SelectContent>
              {(workflowRoles ?? []).map((r) => (
                <SelectItem key={r._id} value={r._id}>
                  <RoleBadge role={r} size="sm" />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <p className="mt-2 text-xs text-primary/50">{MODE_HELP[current.mode]}</p>
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-primary/60">
          Primary Contact Defaults To
        </label>
        <p className="text-xs text-primary/40">
          Apply a primary contact override (e.g. person who generated docs)
          across all document generation.
        </p>
        {controls}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-background p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/5">
          <User className="size-4 text-primary/60" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-primary">
            Primary Contact Defaults To
          </h4>
          <p className="text-xs text-primary/40">
            Who the day-one contact name, email and direct phone come from (for
            example the insurer&apos;s &ldquo;IP file manager or primary
            contact&rdquo;). Analysts can change it for a single generation.
          </p>
          {controls}
        </div>
      </div>
    </div>
  );
}
